<template>
  <component
    :is="tag"
    :class="classesRoot"
    :type="internalType"
    :disabled="disabled"
    v-on="$listeners"
  >
    <span
      :class="$style.inner"
      :style="innerStyle"
    >
      <UiIcon
        v-if="theme == 'hasLesson'"
        :class="$style.iconHasLesson"
        :color-inheritance="true"
        :name="'check'"
      />
      <slot />
    </span>
  </component>
</template>

<script>
import UiIcon from '@/components/UiIcon';

  const tagValidator = (_) => ['button', 'a', 'div'].includes(_);
  const typeValidator = (_) => ['button', 'submit', 'reset'].includes(_);
  const sizeValidator = (_) => ['small', 'normal', 'large'].includes(_);
  const themeValidator = (_) => [
    'silent',
    'gray',
    'hasLesson',
    'danger',
    'modal',
    'background-brand',
    'outline-brand',
  ].includes(_);
  const textAlignValidator = (_) => [
    'left',
    'center',
    'right',
    'justify',
  ].includes(_);

  export default {
    name: 'UiButton',
    components: {
      UiIcon,
    },
    props: {
      tag: {
        type: String,
        default: 'button',
        validate: tagValidator,
      },
      type: {
        type: String,
        default: 'button',
        validate: typeValidator,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      theme: {
        type: String,
        default: null,
        validate: themeValidator,
      },
      fluid: {
        type: Boolean,
        default: false,
      },
      nowrapped: {
        type: Boolean,
        default: true,
      },
      squared: {
        type: Boolean,
        default: false,
      },
      depressed: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: 'normal',
        validate: sizeValidator,
      },
      flat: {
        type: Boolean,
        default: false,
      },
      textAlign: {
        type: String,
        default: 'center',
        validate: textAlignValidator,
      },
      innerStyle: {
        type: [Object, String],
        default: null,
      },
    },
    computed: {
      classesRoot() {
        const {
          $style,
          size,
          theme,
          flat,
          textAlign,
        } = this;
        return {
          [$style.root]: true,
          [$style.isFluid]: this.fluid,
          [$style.isNoWrap]: this.nowrapped,
          [$style.isSquared]: this.squared,
          [$style.isFlat]: flat,
          [$style.isDepressed]: ((this.depressed && !flat) || this.outline),
          [$style.isDisabled]: this.disabled,
          [$style.sizeSmall]: size === 'small',
          [$style.sizeNormal]: size === 'normal',
          [$style.sizeLarge]: size === 'large',
          [$style.themeSilent]: theme === 'silent',
          [$style.themeGray]: theme === 'gray',
          [$style.themeGreen]: theme === 'green',
          [$style.themeDanger]: theme === 'danger',
          [$style.themeBackgroundBrand]: theme === 'background-brand',
          [$style.themeModal]: theme === 'modal',
          [$style.themeOutlineBrand]: theme === 'outline-brand',
          [$style.themeHasLesson]: theme === 'hasLesson',
          [$style.textAlignLeft]: textAlign === 'left',
          [$style.textAlignCenter]: textAlign === 'center',
          [$style.textAlignRight]: textAlign === 'right',
          [$style.textAlignJustify]: textAlign === 'justify',
        };
      },
      internalType() {
        return (this.tag === 'button') ? this.type : null;
      },
    },
    created() {
    },
  };
</script>

<style lang="scss" module>
  .root {
    @include resetButton;

    display: inline-flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 auto;
    border-radius: 4px;
    font-weight: 400;
    text-align: center;
    text-decoration: none;
    transition: $primaryTransition;
    position: relative;
    outline: none;
    vertical-align: middle;
    user-select: none;

    &.isFluid {
      display: flex;
      flex: 1;
      width: 100%;
      @include marginX(0);
    }

    &.sizeSmall {
      @include typography(14px, 12px);
    }

    &.sizeNormal {
      @include typography(17px, 16px);
    }

    &.sizeLarge {
      @include typography(20px, 18px);
    }

    &.isSquared {
      border-radius: 0;
    }

    &.isDisabled {
      opacity: 0.3;
      //color: #cecece;
      pointer-events: none;
    }

    &:hover {
      z-index: 1;
    }

    &:not(.isDepressed):not(.isFlat):not(.themeSilent) {
      box-shadow: 0 1px 8px 0 transparent;

      &:hover {
        &:not([disabled]) {
          background-color: #DFEFCE;
        }
      }

      &:active {
        &:not([disabled]) {
          background-color: #DFEFCE;
        }
      }
    }
  }

  .inner {
    display: flex;
    align-items: center;
    flex: 1 0 auto;
    width: inherit;
    border-radius: inherit;
    //padding: 12px;
    color: inherit;
    position: relative;
    box-sizing: border-box;

    .isNoWrap &{
      white-space: nowrap;
    }

    .sizeSmall & {
      padding: 8px;
    }

    .sizeLarge & {
      padding: 16px;
    }

    .textAlignLeft & {
      justify-content: flex-start;
    }

    .textAlignCenter & {
      justify-content: center;
    }

    .textAlignRight & {
      justify-content: flex-end;
    }

    .textAlignJustify & {
      justify-content: space-between;
    }
  }

  .themeSilent {
    background-color: unset  !important;
    cursor: default !important;
    opacity: 0.3;
    & .inner {
      width: auto;
    }
  }

  .themeModal {
    background-color: $colorDarkBlue;
    color: $colorWhite;

    & .inner {
      font-weight: 700;
      font-size: 12px;
      line-height: 18px;
      text-align: center;
    }
  }

  .themeGray {
    border-radius: 14px;
    background-color: $colorGrayLight;

    &.isDepressed,
    &.isFlat {
      background-color: $colorGrayLight;

      &:hover {
        &:not([disabled]) {
          background-color: darken($colorGrayLight, 5%);
        }
      }

      &:active {
        &:not([disabled]) {
          background-color: darken($colorGrayLight, 5%);
        }
      }
    }
    @include media('xs') {
      background-color: unset;
    }
  }
  .themeGreen {
    border-radius: 10px;
    padding: 6px 0 6px 0;
    background-color: $colorGreen;

    &.isDepressed,
    &.isFlat {
      background-color: $colorGreen;

      &:hover {
        &:not([disabled]) {
          background-color: darken($colorGreen, 5%);
        }
      }

      &:active {
        &:not([disabled]) {
          background-color: darken($colorGreen, 5%);
        }
      }
    }
  }
  .iconHasLesson {
    position: absolute;
    left: 15px;
    font-size: 20px;
    padding-right: 6px;
    color: $colorGreen;
  }
  .themeDanger {
    border-radius: 10px;
    padding: 6px 0 6px 0;
    background-color: $colorDanger;
    color: $colorWhite;

    &:hover {
      &:not([disabled]) {
        background-color:   darken($colorDanger, 5%) !important;
      }
    }

    &:active {
      &:not([disabled]) {
        background-color:  darken($colorDanger, 5%) !important;
      }
    }
  }
  .themeBackgroundBrand,
  .themeOutlineBrand {
    &::before {
      content: '';
      display: block;
      @include gradientBrand;
      border-radius: inherit;
      position: absolute;
      @include positionAll(0);
    }

    &.isDisabled::before {
      opacity: .5;
      filter: grayscale(1);
    }
  }

  .themeBackgroundBrand {
    & .inner {
      color: $colorWhite;
    }
  }

  .themeOutlineBrand {
    & .inner {
      width: auto;
      background-color: $colorWhite;
      border-radius: 2px;
      padding: 10px;
      margin: 2px;
    }

    &.sizeSmall .inner {
      padding: 6px;
    }

    &.sizeLarge .inner {
      padding: 14px;
    }
  }
  .themeHasLesson {
    & .inner {
      //width: fit-content;
    }
  }
</style>
